import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { m } from 'framer-motion'
import { AlertCircleIcon, CheckCircleIcon, InfoIcon, Loader2Icon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import useSWR, { mutate } from 'swr'

import { SubscriptionPlan, SubscriptionStatus } from 'core/remodel/types/billing'
import { DateFormat } from 'core/remodel/types/common'
import { DocNotExist } from 'core/remodel/types/error'
import { currencyOptions } from 'core/remodel/types/options'
import { fetchCurrentPreferences, fetchSubscription, updatePreferences, userQuery } from '@/api/AccountService'
import { formatDate } from '@/utils/formatter'
import { parseErrorMessage } from '@/utils/parseErrorMessages'
import tracking from '@/utils/track'
import { useDebounce } from '@/hooks/useDebounce'
import { useToast } from '@/hooks/useToast'
import { useAuthStore } from '@/store/authStore'
import { PreferenceValues } from '@/pages/account/profile/preferences'
import { Button, FormSelect, Modal, SelectItem, Tooltip, TooltipContent, TooltipTrigger } from '@/components/base'
import EnrollTotp from '@/components/EnrollTotp'
import { StaticImage } from '@/components/Image'

export default function Setup() {
  const { delegatorId } = useAuthStore((state) => state.permissions)

  return !delegatorId ? <SelfSetup /> : <DelegatorSetup />
}

/* self */
type SelfCheckEvent = 'subscription' | 'baseCurrency' | 'enrollTotp'

function SelfSetup() {
  const subscriptionSWR = useSWR('checkNotSubscribed', checkNotSubscribed, {
    errorRetryCount: 5,
    errorRetryInterval: 1000
  })
  const baseCurrencySWR = useSWR('checkEmptyBaseCurrency', checkEmptyBaseCurrency)
  const isFirstLoginSWR = useSWR('checkEnrollTotp', checkEnrollTotp)

  const checkEvent = useMemo(() => {
    if ([subscriptionSWR, baseCurrencySWR, isFirstLoginSWR].some(({ data }) => data === undefined)) return undefined
    const entrySet: Record<SelfCheckEvent, boolean> = {
      subscription: subscriptionSWR.data!,
      baseCurrency: baseCurrencySWR.data!,
      enrollTotp: isFirstLoginSWR.data!
    }
    const activeEntry = (Object.entries(entrySet) as [SelfCheckEvent, boolean][]).find(([_key, value]) => value)
    if (!activeEntry) return undefined
    const [event] = activeEntry
    return event
  }, [subscriptionSWR, baseCurrencySWR, isFirstLoginSWR])

  useEffect(() => {
    const allFalse = [subscriptionSWR, baseCurrencySWR, isFirstLoginSWR].every(({ data }) => data === false)

    // setup done
    if (allFalse) {
      useAuthStore.setState({ isSetupDone: true })
    }
  }, [subscriptionSWR, baseCurrencySWR, isFirstLoginSWR])

  switch (checkEvent) {
    case 'subscription': {
      return <SetupSubscription />
    }
    case 'baseCurrency': {
      return <SetupBaseCurrency />
    }
    case 'enrollTotp': {
      return <EnrollTotp />
    }
    // loading
    default: {
      return (
        <m.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={'relative grid h-screen w-full place-items-center'}
        >
          <Loader2Icon className={'h-9 w-9 animate-spin text-primary'} />
        </m.main>
      )
    }
  }
}

// Helper function to create a promise that rejects after a timeout
function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`Operation timed out after ${timeoutMs}ms`))
    }, timeoutMs)

    promise
      .then((result) => {
        clearTimeout(timeoutId)
        resolve(result)
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        reject(error)
      })
  })
}

async function checkNotSubscribed(): Promise<boolean | undefined> {
  try {
    const database = useAuthStore.getState().database
    if (!database) {
      return undefined
    }

    const isBack = new URL(window.location.href).searchParams.get('isBack') === 'true'
    if (isBack) {
      return true
    }

    const timeoutMs = 3000

    const subscription = await withTimeout(
      fetchSubscription(database)([userQuery.subscription]).then((data) => ({
        isSubscribed: data.status === SubscriptionStatus.Successful
      })),
      timeoutMs
    )
    return !subscription.isSubscribed
  } catch (e) {
    console.warn('checkSubscription error', e)
    // HACK TODO: We shouldn't rely on error message strings for logic
    if ((e as Error).message.includes('Empty subscription')) {
      return true
    } else if ((e as Error).message.includes('timed out')) {
      console.warn(`[${new Date().toISOString()}] checkNotSubscribed - TIMEOUT`)
    }

    throw e
  }
}
async function checkEmptyBaseCurrency(): Promise<boolean> {
  const database = useAuthStore.getState().database
  if (!database) throw new Error('No database')
  const { baseCurrency } = await fetchCurrentPreferences(database)([userQuery.currentPreferences])
  // console.log('checkEmptyBaseCurrency', !!baseCurrency)
  return !baseCurrency
}

async function checkEnrollTotp(): Promise<boolean> {
  const database = useAuthStore.getState().database
  if (!database) throw new Error('No database')
  return database.checkRemindMFA()
}

enum SubscriptionModalKind {
  TrialStarted = 'trialStarted',
  SubscriptionStarted = 'subscriptionStarted',
  SubscriptionCanceled = 'subscriptionCanceled',
  SubscriptionFailed = 'subscriptionFailed',
  Failed = 'Failed',
  CycleBillEnded = 'cycleBillEnded'
}
function SetupSubscription() {
  const { t } = useTranslation()
  const searchParams = useSearchParams()
  const isBack = searchParams.get('isBack') === 'true'
  const [kind, setKind] = useState<SubscriptionModalKind | null>(null)
  const database = useAuthStore((state) => state.database)
  const { delegatorId } = useAuthStore((state) => state.permissions)

  const { data, error } = useSWR([userQuery.subscription, delegatorId], fetchSubscription(database!), {
    errorRetryInterval: 1000
  })

  useEffect(() => {
    const checkStatus = async () => {
      if (isBack && error) return
      if (data) {
        const { plan, status } = data
        switch (plan) {
          case SubscriptionPlan.Trial: {
            if (status === SubscriptionStatus.Successful) {
              setKind(SubscriptionModalKind.TrialStarted)
            } else if (status === SubscriptionStatus.Failed) {
              setKind(SubscriptionModalKind.Failed)
            } else if (status === SubscriptionStatus.Canceled || status === SubscriptionStatus.Expired) {
              setKind(SubscriptionModalKind.CycleBillEnded)
            }
            break
          }

          case SubscriptionPlan.Subscribed: {
            if (status === SubscriptionStatus.Successful) {
              setKind(SubscriptionModalKind.SubscriptionStarted)
            } else if (status === SubscriptionStatus.Failed) {
              setKind(SubscriptionModalKind.Failed)
            } else if (status === SubscriptionStatus.Canceled || status === SubscriptionStatus.Expired) {
              setKind(SubscriptionModalKind.CycleBillEnded)
            }
            break
          }
        }
      } else if (error) {
        if (
          error instanceof DocNotExist ||
          (error.message && error.message.includes('not exist')) ||
          (error.message && error.message.includes('Empty subscription'))
        ) {
          const url = new URL(window.location.href)
          const returnUrl = `${url.origin}/?isBack=true`
          const result = await database!.getStripeCheckoutSessionUrl({ returnUrl })
          window.location.href = result.url
        } else {
          return
        }
      }
    }

    if (database) checkStatus()
  }, [isBack, data, error, database])

  const handleSetup = async () => {
    // clear isBack parameter to avoid infinite loop
    const url = new URL(window.location.href)
    url.searchParams.delete('isBack')
    window.history.replaceState({}, document.title, url.toString())
    // revalidate the subscription status and base currency to avoid stale data
    await Promise.all([
      mutate('checkNotSubscribed', false, { revalidate: false }),
      mutate('checkEmptyBaseCurrency', undefined, { revalidate: true })
    ])
  }
  const handleResubscribe = async () => {
    const url = new URL(window.location.href)
    const returnUrl = `${url.origin}/?from=stripe-subscription`
    const result = await database!.getStripePortalSessionUrl({ returnUrl })
    window.location.href = result.url
  }
  const expiryDate = data?.expiryDate
  const formattedDate = formatDate(expiryDate, DateFormat.MMMDDYYYY, undefined, true)
  return (
    <Modal className={'max-w-[460px] rounded-lg'}>
      {kind === null && (
        <Modal.Content className={'h-60 place-items-center'}>
          <Loader2Icon className={'h-8 w-8 animate-spin text-primary'} />
        </Modal.Content>
      )}

      {kind === SubscriptionModalKind.TrialStarted && (
        <Modal.Content className={'gap-y-4 text-center'}>
          <CheckCircleIcon className={'mx-auto text-primary'} size={40} />
          <label className={'text-xl font-semibold text-[#21253B]'}>{t('account:TrialActivated')}</label>
          <p className={'text-sm text-[#424553]'}>{t('account:FreeTrialHasStarted')}</p>
          <Button className={'py-3'} variant={'solid'} size={'md'} onClick={handleSetup}>
            {t('Continue')}
          </Button>
        </Modal.Content>
      )}

      {kind === SubscriptionModalKind.SubscriptionStarted && (
        <Modal.Content className={'gap-y-4 text-center'}>
          <CheckCircleIcon className={'mx-auto text-primary'} size={40} />
          <label className={'text-xl font-semibold text-[#21253B]'}>{t('account:SubscriptionActivated')}</label>
          <p className={'text-sm text-[#424553]'}>{t('account:PaymentHasBeenProcessed')}</p>
          <Button className={'py-3'} variant={'solid'} size={'md'} onClick={handleSetup}>
            {t('Continue')}
          </Button>
        </Modal.Content>
      )}

      {kind === SubscriptionModalKind.Failed && (
        <Modal.Content className={'gap-y-4 text-center'}>
          <AlertCircleIcon className={'mx-auto text-destructive'} size={40} />
          <label className={'text-xl font-semibold text-[#21253B]'}>{t('account:AccountReactivationFailed')}</label>
          <p className={'text-sm text-[#424553]'}>{t('account:CheckYourPaymentDetails')}</p>
          <Button className={'py-3'} variant={'solid'} size={'md'} onClick={handleResubscribe}>
            {t('Continue')}
          </Button>
        </Modal.Content>
      )}

      {kind === SubscriptionModalKind.CycleBillEnded && (
        <Modal className={'max-w-lg'}>
          <Modal.Content className={'gap-y-4 text-center'}>
            <AlertCircleIcon className={'mx-auto text-destructive'} size={40} />
            <label className={'text-xl font-semibold text-[#21253B]'}>{t('account:SubscriptionPlanEnded')}</label>
            <p className={'text-sm text-[#424553]'}>{t('account:SubscriptionPlanEndedDescriptionP1')}</p>
            <Trans
              t={t}
              i18nKey={'account:SubscriptionPlanEndedDescriptionP2'}
              parent={(props: any) => <p className={'text-sm text-[#424553]'} {...props} />}
              components={{
                strong: <span className={'text-base font-semibold'} />
              }}
              values={{ date: formattedDate }}
            />
            <Button className={'py-3'} variant={'solid'} size={'md'} onClick={handleResubscribe}>
              {t('account:ReactivateAccountNow')}
            </Button>
          </Modal.Content>
        </Modal>
      )}
    </Modal>
  )
}

type BaseCurrencyStep = 'confirm' | 'result'

function SetupBaseCurrency() {
  const { t } = useTranslation()
  const { toast } = useToast()

  // step state
  const [step, setStep] = useState<BaseCurrencyStep | null>(null)
  const debouncedStep = useDebounce(step, 100)

  // form state
  const database = useAuthStore((state) => state.database)
  const { data: preferences } = useSWR([userQuery.currentPreferences], fetchCurrentPreferences(database!))
  const form = useForm<PreferenceValues>({ values: preferences })

  const onConfirm = async (values: PreferenceValues) => {
    try {
      await updatePreferences(database!, values)
      await mutate('checkEmptyBaseCurrency')
    } catch (e) {
      const errorMessage = parseErrorMessage(e, 'Unknown error: Update Preferences')
      toast({ variant: 'error', description: errorMessage })
    }
  }

  return (
    !preferences?.baseCurrency && (
      <>
        <Modal className={'max-w-xl'}>
          <Modal.Content className={'gap-4'}>
            <div className={'relative flex min-h-[320px] flex-col items-center justify-center gap-y-4'}>
              <StaticImage
                className={'pointer-events-none'}
                src={'/images/finances/global-map-375w.webp'}
                alt={'Global map'}
                srcSet={`
                  /images/finances/global-map-375w.webp 375w, 
                  /images/finances/global-map-480w.webp 480w, 
                  /images/finances/global-map-544w.webp 544w
                `}
                sizes={`
                  (max-width: 480px) calc(100vw - 32px), 
                  (max-width: 608px) 480px, 
                  544px
                `}
                fill={true}
              />
              <div className={'flex flex-col items-center gap-y-4'}>
                <p className={'text-2xl text-primary'}>{t('WelcomeToMyAssets')}</p>
                <p className={'text-center'}>{t('SetBaseCurrency')}</p>
                <div className={'flex items-center gap-4'}>
                  <FormSelect
                    control={form.control}
                    name={'baseCurrency'}
                    className={'w-40'}
                    placeholder={t('Select')}
                    rules={{ required: true }}
                  >
                    {currencyOptions.map((option) => (
                      <SelectItem key={option.value} value={`${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </FormSelect>

                  <Tooltip>
                    <TooltipTrigger asChild={true}>
                      <InfoIcon className={'cursor-pointer fill-primary text-white'} size={24} />
                    </TooltipTrigger>
                    <TooltipContent className={'rounded-sm border-0 bg-primary px-2 py-1'}>
                      <span className={'whitespace-pre text-xs font-medium text-white'}>
                        {t('SelectCantBeReserved')}
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
            <fieldset className={'flex flex-col-reverse justify-end gap-2 md:flex-row [&>button]:min-w-[130px]'}>
              <Button
                className={'group relative'}
                variant={'solid'}
                size={'md'}
                type={'submit'}
                onClick={() => setStep('confirm')}
                disabled={!form.watch('baseCurrency')}
              >
                {t('Confirm')}
              </Button>
            </fieldset>
          </Modal.Content>
        </Modal>

        {debouncedStep === 'confirm' && (
          <Modal className={'max-w-md'}>
            <Modal.Header className={'bg-primary'}>
              <label className={'text-sm font-medium uppercase text-white'}>{t('ConfirmSelection')}</label>
            </Modal.Header>
            <Modal.Content>
              <div className={'space-y-4'}>
                <div className={'flex min-h-[50px] flex-col items-center justify-center gap-4'}>
                  <Trans
                    t={t}
                    i18nKey={'ConfirmBaseCurrencyContent'}
                    parent={(props: any) => (
                      <span className={'max-w-[416px] break-words text-center text-sm text-gray-600'} {...props} />
                    )}
                    values={{ currency: form.watch('baseCurrency') }}
                    components={{ bold: <strong /> }}
                  />
                </div>
                <fieldset
                  className={'flex flex-col-reverse justify-center gap-2 md:flex-row [&>button]:min-w-[130px]'}
                  disabled={form.formState.isSubmitting}
                >
                  <Button variant={'outline'} size={'md'} onClick={() => setStep(null)}>
                    {t('Cancel')}
                  </Button>
                  <Button
                    className={'group relative'}
                    variant={'solid'}
                    size={'md'}
                    onClick={form.handleSubmit(onConfirm)}
                  >
                    <Loader2Icon className={'absolute animate-spin opacity-0 group-disabled:opacity-100'} />
                    <span className={'group-disabled:opacity-0'}>{t('Confirm')}</span>
                  </Button>
                </fieldset>
              </div>
            </Modal.Content>
          </Modal>
        )}
      </>
    )
  )
}

/* delegator */
type DelegateCheckEvent = 'mock'

function DelegatorSetup() {
  const checkEvent = useMemo(() => {
    const entrySet: Record<DelegateCheckEvent, boolean> = {
      mock: false
    }
    const activeEntry = (Object.entries(entrySet) as [DelegateCheckEvent, boolean][]).find(([_key, value]) => value)
    if (!activeEntry) return undefined
    const [event] = activeEntry
    return event
  }, [])

  useEffect(() => {
    // setup done
    if ([].every(({ data }) => data === false)) {
      useAuthStore.setState({ isSetupDone: true })
    }
  }, [])

  switch (checkEvent) {
    // loading
    default: {
      return (
        <m.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={'relative grid h-screen w-full place-items-center'}
        >
          <Loader2Icon className={'h-9 w-9 animate-spin text-primary'} />
        </m.main>
      )
    }
  }
}
